// Shared presentational building blocks for TalentConnect pages.
import React, { useRef } from 'react';
import { C, seniorityColor, seniorityLabel } from './talentConnect';

export const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
export const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 11px',
  outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
export const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
  textTransform: 'uppercase', display: 'block', marginBottom: '5px',
};
export const btnPrimary: React.CSSProperties = {
  background: C.navy, color: C.white, border: 'none', borderRadius: '3px',
  padding: '9px 20px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
export const btnGhost: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
  borderRadius: '3px', padding: '8px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};

export function Banner({ type, text }: { type: 'ok' | 'err' | 'info'; text: string }) {
  const map = {
    ok: { bg: '#F0FDF4', bd: '#BBF7D0', fg: C.success },
    err: { bg: '#FEF2F2', bd: '#FECACA', fg: C.danger },
    info: { bg: '#FEFCE8', bd: '#FDE68A', fg: C.amber },
  }[type];
  return (
    <div style={{ background: map.bg, border: `1px solid ${map.bd}`, borderRadius: '3px', padding: '9px 12px', marginBottom: '16px', fontSize: '12.5px', color: map.fg, lineHeight: 1.5 }}>{text}</div>
  );
}

// Pill multi-select toggle group.
export function ChipSelect({ options, selected, onToggle, columns }: {
  options: (string | { value: string; label: string })[];
  selected: string[];
  onToggle: (v: string) => void;
  columns?: number;
}) {
  return (
    <div style={columns
      ? { display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: '6px' }
      : { display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {options.map(opt => {
        const value = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const on = selected.includes(value);
        return (
          <button key={value} type="button" onClick={() => onToggle(value)} style={{
            padding: '6px 12px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer',
            fontFamily: 'system-ui', fontWeight: 500, textAlign: columns ? 'left' : 'center',
            border: `1px solid ${on ? C.navy : C.border}`, background: on ? C.navy : C.white,
            color: on ? C.white : C.textSec,
          }}>{on ? '✓ ' : ''}{label}</button>
        );
      })}
    </div>
  );
}

// Free-text tag input (skills).
export function TagInput({ tags, onChange, placeholder }: {
  tags: string[]; onChange: (t: string[]) => void; placeholder?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const add = () => {
    const v = ref.current?.value.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    if (ref.current) ref.current.value = '';
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: tags.length ? '8px' : 0, flexWrap: 'wrap' }}>
        {tags.map(t => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: C.surface, border: `1px solid ${C.border}`, color: C.textSec, fontSize: '12px', fontWeight: 500, padding: '3px 8px', borderRadius: '3px' }}>
            {t}
            <button type="button" onClick={() => onChange(tags.filter(x => x !== t))} style={{ background: 'none', border: 'none', color: C.mutedLt, cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input ref={ref} placeholder={placeholder || 'Add a skill and press Enter'} style={inputStyle}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" onClick={add} style={{ ...btnGhost, flexShrink: 0 }}>Add</button>
      </div>
    </div>
  );
}

export function SeniorityBadge({ seniority }: { seniority: string | null }) {
  const color = seniorityColor(seniority);
  return (
    <span style={{ background: `${color}14`, color, border: `1px solid ${color}33`, padding: '2px 8px', borderRadius: '2px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {seniorityLabel(seniority)}
    </span>
  );
}

export function Pills({ items, max, bg, fg, bd }: { items: string[]; max?: number; bg?: string; fg?: string; bd?: string }) {
  const shown = max ? items.slice(0, max) : items;
  const extra = max && items.length > max ? items.length - max : 0;
  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {shown.map(r => (
        <span key={r} style={{ background: bg || C.surface, border: `1px solid ${bd || C.border}`, color: fg || C.textSec, fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '2px' }}>{r}</span>
      ))}
      {extra > 0 && <span style={{ color: C.mutedLt, fontSize: '10px', fontWeight: 600, alignSelf: 'center' }}>+{extra}</span>}
    </div>
  );
}

export function LoadingScreen({ label }: { label?: string }) {
  return (
    <div style={{ display: 'flex', height: '60vh', alignItems: 'center', justifyContent: 'center', background: C.surface }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>{label || 'Loading…'}</div>
    </div>
  );
}

export function fmtDate(ts: string | null): string {
  return ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
}
